import React from "react";
import type { ModeRendererProps } from "./types";

export function VasarelyGpt5Mode({ brand, today, Copy }: ModeRendererProps) {
  // Namespaced IDs to avoid collisions
  const idBase = React.useMemo(() => {
    try {
      const url = typeof window !== "undefined" ? new URL(window.location.href) : null;
      const param = url
        ? url.searchParams.get("id") || url.searchParams.get("uid") || url.searchParams.get("modeId") || url.searchParams.get("mode_id")
        : null;
      return param ? `gpt5_${param}` : `gpt5_${Math.random().toString(36).slice(2, 8)}`;
    } catch {
      return `gpt5_${Math.random().toString(36).slice(2, 8)}`;
    }
  }, []);
  const gradId = `${idBase}_grad`;
  const glowId = `${idBase}_glow`;

  const defs = (
    <defs>
      <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#10A37F" />
        <stop offset="50%" stopColor="#7C3AED" />
        <stop offset="100%" stopColor="#C084FC" />
      </linearGradient>
      <radialGradient id={glowId} cx="50%" cy="50%">
        <stop offset="0%" stopColor="#C084FC" stopOpacity={0.7} />
        <stop offset="100%" stopColor="#10A37F" stopOpacity={0} />
      </radialGradient>
    </defs>
  );

  // Activation mosaics: neural firing patterns
  const activationMosaics = [];
  for (let row = 0; row < 16; row++) {
    for (let col = 0; col < 12; col++) {
      const x = 80 + col * 55;
      const y = 120 + row * 55;
      const activation = Math.sin(row * 0.4 + col * 0.6) * 0.5 + 0.5;
      const size = 20 + activation * 15;
      activationMosaics.push(
        <rect
          key={`mosaic-${row}-${col}`}
          x={x - size / 2}
          y={y - size / 2}
          width={size}
          height={size}
          fill={`url(#${gradId})`}
          opacity={0.3 + activation * 0.4}
          rx={4}
        />
      );
    }
  }

  // Attention tessellation: self-attention mechanism
  const attentionTessellation = [];
  for (let i = 0; i < 10; i++) {
    const angle = (i * Math.PI * 2) / 10;
    const radius = 200;
    const x1 = 397 + radius * Math.cos(angle);
    const y1 = 561.5 + radius * Math.sin(angle);
    for (let j = i + 1; j < 10; j++) {
      const angle2 = (j * Math.PI * 2) / 10;
      const x2 = 397 + radius * Math.cos(angle2);
      const y2 = 561.5 + radius * Math.sin(angle2);
      attentionTessellation.push(
        <line
          key={`attention-${i}-${j}`}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={`url(#${gradId})`}
          strokeWidth={0.5}
          opacity={0.1}
        />
      );
    }
  }

  // Reasoning waves: thought propagation
  const reasoningWaves = [];
  for (let i = 0; i < 14; i++) {
    const radius = 50 + i * 25;
    const segments = 24;
    const points = [];
    for (let j = 0; j < segments + 1; j++) {
      const angle = (j * Math.PI * 2) / segments;
      const wave = Math.sin(j * 2 + i * 0.5) * 10;
      const r = radius + wave;
      points.push(`${397 + r * Math.cos(angle)},${561.5 + r * Math.sin(angle)}`);
    }
    reasoningWaves.push(
      <polyline
        key={`wave-${i}`}
        points={points.join(" ")}
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={1.5}
        opacity={0.3}
      />
    );
  }

  // Transformer layers: architecture depth
  const transformerLayers = [];
  for (let i = 0; i < 12; i++) {
    const y = 140 + i * 75;
    const width = 600 - i * 30;
    const x = 397 - width / 2;
    transformerLayers.push(
      <rect
        key={`layer-${i}`}
        x={x}
        y={y}
        width={width}
        height={40}
        fill="none"
        stroke="url(#gpt5Grad)"
        strokeWidth={1.5}
        opacity={0.2}
        rx={8}
      />
    );
  }

  // Central consciousness core
  const consciousnessCore = (
    <g opacity={0.9}>
  <circle cx={397} cy={561.5} r={120} fill={`url(#${glowId})`} />
      <circle cx={397} cy={561.5} r={80} fill="none" stroke="#C084FC" strokeWidth={3} />
      <circle cx={397} cy={561.5} r={55} fill="none" stroke="#7C3AED" strokeWidth={2} />
      <circle cx={397} cy={561.5} r={35} fill="none" stroke="#10A37F" strokeWidth={2} />
      <text
        x={397}
        y={575}
        fontFamily="monospace"
        fontSize={32}
        fontWeight="bold"
        fill="#FFFFFF"
        textAnchor="middle"
      >
        GPT
      </text>
    </g>
  );

  return (
    <g>
      {defs}
      {transformerLayers}
      {activationMosaics}
      {attentionTessellation}
      {reasoningWaves}
      {consciousnessCore}

      <g className="mode-copy" transform="translate(48, 840)" fill="#FFFFFF" fontFamily="monospace">
        <text x={0} y={0} fontSize={18} fontWeight="bold">
          {Copy?.title}
        </text>
        <text x={0} y={22} fontSize={14}>
          {Copy?.subtitle}
        </text>
        <text x={0} y={44} fontSize={12} opacity={0.85}>
          {Copy?.date}
        </text>
      </g>
    </g>
  );
}
